import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, CalendarIcon, CheckCircle, XCircle, LogIn, LogOut, Building2, Search, Printer, Download, AlertTriangle, FileText, Truck, Edit, ArrowRightLeft
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';

import API from '@/config/api';
import ParentCheckinQRPoster from '@/components/ParentCheckinQRPoster';

const AttendancePage = () => {
  const { getAuthHeaders } = useAuth();
  const [children, setChildren] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [shiftFilter, setShiftFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'not_in' | 'present'
  
  // Check-in dialog state
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedClassroom, setSelectedClassroom] = useState('');
  const [transportType, setTransportType] = useState('');
  
  // Print report state
  const [printMode, setPrintMode] = useState(false);
  const [reportStartDate, setReportStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reportEndDate, setReportEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reportData, setReportData] = useState([]);
  const [reportMoves, setReportMoves] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  
  // Edit attendance state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editCheckIn, setEditCheckIn] = useState('');
  const [editCheckOut, setEditCheckOut] = useState('');
  const [editTransportIn, setEditTransportIn] = useState('');
  const [editTransportOut, setEditTransportOut] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [moveChildId, setMoveChildId] = useState(null);
  const [moveTarget, setMoveTarget] = useState('');
  const [moveTime, setMoveTime] = useState('');
  const [shiftTimes, setShiftTimes] = useState([]);
  const [moveHistory, setMoveHistory] = useState({});
  const [editingMove, setEditingMove] = useState(null);
  const [editMoveTime, setEditMoveTime] = useState('');
  const [editMoveNotes, setEditMoveNotes] = useState('');
  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    return `${hr12}:${m} ${ampm}`;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [childrenRes, attendanceRes, classroomsRes, shiftTimesRes] = await Promise.all([
        axios.get(`${API}/children`, getAuthHeaders()),
        axios.get(`${API}/attendance?date=${dateStr}`, getAuthHeaders()),
        axios.get(`${API}/classrooms`, getAuthHeaders()),
        axios.get(`${API}/settings/shift-times`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setChildren(childrenRes.data);
      setAttendance(attendanceRes.data);
      setClassrooms(classroomsRes.data);
      setShiftTimes(shiftTimesRes.data || []);
    } catch (error) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const openCheckInDialog = (child) => {
    setSelectedChild(child);
    setSelectedClassroom(child.classroom || '');
    setTransportType('');
    setCheckInDialogOpen(true);
  };

  const handleCheckIn = async () => {
    if (!selectedChild) return;
    
    try {
      // First update child's classroom if changed
      if (selectedClassroom && selectedClassroom !== selectedChild.classroom) {
        await axios.put(`${API}/children/${selectedChild.id}`, {
          ...selectedChild,
          classroom: selectedClassroom,
          allergies: selectedChild.allergies || []
        }, getAuthHeaders());
      }
      
      // Then check in with transport info
      await axios.post(`${API}/attendance/check-in?child_id=${selectedChild.id}`, {
        classroom: selectedClassroom,
        transport_type: transportType
      }, getAuthHeaders());
      toast.success(`${selectedChild.first_name} checked in to ${selectedClassroom || selectedChild.classroom}`);
      setCheckInDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  const handleQuickCheckIn = async (childId) => {
    try {
      await axios.post(`${API}/attendance/check-in?child_id=${childId}`, {}, getAuthHeaders());
      toast.success('Check-in recorded');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  const handleCheckOut = async (childId) => {
    try {
      const res = await axios.post(`${API}/attendance/check-out?child_id=${childId}`, {}, getAuthHeaders());
      if (res.data.warning) {
        toast.warning(res.data.warning, { duration: 10000 });
      } else {
        toast.success('Check-out recorded');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    }
  };

  const downloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const res = await axios.get(
        `${API}/attendance/report/pdf?start_date=${reportStartDate}&end_date=${reportEndDate}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `DCF_Attendance_${reportStartDate}_to_${reportEndDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const openEditDialog = (record, child) => {
    setEditRecord({ ...record, child_name: child ? `${child.first_name} ${child.last_name}` : record.child_name });
    // Convert ISO times to HH:MM for the time input
    const toTimeStr = (iso) => {
      if (!iso) return '';
      try { return new Date(iso).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }); }
      catch { return ''; }
    };
    setEditCheckIn(toTimeStr(record.check_in_time));
    setEditCheckOut(toTimeStr(record.check_out_time));
    setEditTransportIn(record.transport_type || '');
    setEditTransportOut(record.transport_dropoff_type || '');
    setEditNotes(record.notes || '');
    setEditDialogOpen(true);
  };

  const saveEdit = async () => {
    if (!editRecord) return;
    setSavingEdit(true);
    try {
      // Convert HH:MM back to ISO datetime using the record's date
      const toISO = (timeStr) => {
        if (!timeStr) return '';
        const [h, m] = timeStr.split(':');
        const d = new Date(editRecord.date + 'T00:00:00Z');
        d.setUTCHours(parseInt(h), parseInt(m), 0, 0);
        return d.toISOString();
      };

      await axios.put(`${API}/attendance/${editRecord.id}`, {
        check_in_time: editCheckIn ? toISO(editCheckIn) : undefined,
        check_out_time: editCheckOut ? toISO(editCheckOut) : undefined,
        transport_type: editTransportIn,
        transport_dropoff_type: editTransportOut,
        notes: editNotes
      }, getAuthHeaders());
      toast.success('Attendance record updated');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update record');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleMoveClassroom = async (childId) => {
    if (!moveTarget) return;
    try {
      const now = new Date();
      const moveTimeISO = moveTime 
        ? new Date(`${format(selectedDate, 'yyyy-MM-dd')}T${moveTime}:00`).toISOString()
        : now.toISOString();
      
      await axios.post(`${API}/children/${childId}/moves`, {
        to_classroom: moveTarget,
        move_time: moveTimeISO
      }, getAuthHeaders());
      toast.success('Classroom move recorded');
      setMoveChildId(null);
      setMoveTarget('');
      setMoveTime('');
      fetchData();
    } catch (error) {
      toast.error('Failed to move child');
    }
  };

  const fetchMoveHistory = async (childId) => {
    try {
      const res = await axios.get(`${API}/children/${childId}/moves`, getAuthHeaders());
      setMoveHistory(prev => ({ ...prev, [childId]: res.data }));
    } catch (error) {
      console.error('Failed to fetch move history');
    }
  };

  const handleEditMove = async (childId, moveId) => {
    if (!editMoveTime && !editMoveNotes) return;
    try {
      const updates = {};
      if (editMoveTime) {
        updates.move_time = new Date(`${format(selectedDate, 'yyyy-MM-dd')}T${editMoveTime}:00`).toISOString();
      }
      if (editMoveNotes !== undefined) updates.notes = editMoveNotes;
      
      await axios.put(`${API}/children/${childId}/moves/${moveId}`, updates, getAuthHeaders());
      toast.success('Move record updated');
      setEditingMove(null);
      fetchMoveHistory(childId);
    } catch (error) {
      toast.error('Failed to update move');
    }
  };

  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const generateReport = async () => {
    setLoadingReport(true);
    try {
      const [attendanceRes, movesRes] = await Promise.all([
        axios.get(`${API}/attendance?start_date=${reportStartDate}&end_date=${reportEndDate}`, getAuthHeaders()),
        axios.get(`${API}/attendance/moves-report?start_date=${reportStartDate}&end_date=${reportEndDate}`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setReportData(attendanceRes.data);
      setReportMoves(movesRes.data || []);
      setPrintMode(true);
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setLoadingReport(false);
    }
  };

  const printReport = () => {
    setTimeout(() => window.print(), 300);
  };

  const exportCSV = () => {
    const headers = ['Date', 'Child Name', 'Classroom', 'Check In', 'Check Out', 'Hours', 'Notes'];
    const rows = reportData.map(r => {
      const cin = r.check_in_time ? new Date(r.check_in_time) : null;
      const cout = r.check_out_time ? new Date(r.check_out_time) : null;
      const hours = cin && cout ? ((cout - cin) / 3600000).toFixed(1) : '-';
      return [
        r.date,
        r.child_name,
        r.classroom || '-',
        cin ? cin.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-',
        cout ? cout.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Still In',
        hours,
        r.notes || ''
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${reportStartDate}_to_${reportEndDate}.csv`;
    a.click();
  };

  const getChildAttendance = (childId) => {
    return attendance.find(a => a.child_id === childId);
  };

  if (loading) {
    return (
      <DashboardLayout title="Attendance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Attendance">
      <div className="space-y-6" data-testid="attendance-page">
        {/* Parent self-checkin QR poster (admin/provider only) — desktop only; on mobile the QR takes up too much room at the point of care. */}
        <div className="hidden md:block">
          <ParentCheckinQRPoster />
        </div>

        {/* Date Selector */}
        <Card className="card-clay">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-lg">
                    {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {attendance.length} attendance records
                  </p>
                </div>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="date-picker-btn">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Change Date
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Button variant="outline" onClick={() => setPrintMode(true)} data-testid="print-attendance-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Attendance List */}
        <Card className="card-clay">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="font-heading flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                {isToday ? "Today's Attendance" : 'Attendance Records'}
              </CardTitle>
              {/* Search Bar */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search children..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="attendance-search-input"
                  />
                </div>
                <Select value={shiftFilter} onValueChange={setShiftFilter}>
                  <SelectTrigger className="w-full sm:w-44" data-testid="attendance-shift-filter">
                    <SelectValue placeholder="Filter by shift" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Shifts</SelectItem>
                    {shiftTimes.map((s) => (
                      <SelectItem key={s.id} value={s.name}>{s.name} ({formatTime(s.start_time)}–{formatTime(s.end_time)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Status filter chips — big touch targets on mobile so staff can quickly see who still needs to be checked in. */}
            {isToday && (
              <div className="flex gap-2 mb-4 -mt-1 overflow-x-auto pb-1" data-testid="attendance-status-tabs">
                {[
                  { id: 'all', label: 'All', count: children.length },
                  { id: 'not_in', label: 'Needs check-in', count: children.filter(c => !c.is_checked_in && !getChildAttendance(c.id)).length },
                  { id: 'present', label: 'Present', count: children.filter(c => c.is_checked_in).length },
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setStatusFilter(tab.id)}
                    className={cn(
                      'flex-1 sm:flex-initial min-w-fit whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-colors',
                      statusFilter === tab.id
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                        : 'bg-white text-muted-foreground border-border hover:bg-muted'
                    )}
                    data-testid={`status-tab-${tab.id}`}
                  >
                    {tab.label}
                    <span className={cn(
                      'ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full text-[10px] font-bold',
                      statusFilter === tab.id ? 'bg-white/25' : 'bg-muted-foreground/10'
                    )}>{tab.count}</span>
                  </button>
                ))}
              </div>
            )}
            {(() => {
              const filteredChildren = children.filter(child => {
                if (shiftFilter !== 'all' && (child.usual_shift || 'Full Day') !== shiftFilter) return false;
                if (isToday && statusFilter === 'not_in') {
                  if (child.is_checked_in) return false;
                  if (getChildAttendance(child.id)) return false;
                }
                if (isToday && statusFilter === 'present' && !child.is_checked_in) return false;
                if (!searchQuery.trim()) return true;
                const query = searchQuery.toLowerCase();
                const fullName = `${child.first_name} ${child.last_name}`.toLowerCase();
                return fullName.includes(query);
              });
              
              return filteredChildren.length > 0 ? (
              <div className="space-y-4">
                {filteredChildren.map((child) => {
                  const record = getChildAttendance(child.id);
                  const isCheckedIn = child.is_checked_in;
                  
                  return (
                    <div 
                      key={child.id}
                      className={cn(
                        "flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 rounded-xl border transition-colors gap-3",
                        record ? "bg-green-50 border-green-200" : "bg-muted/50 border-transparent"
                      )}
                      data-testid={`attendance-row-${child.id}`}
                    >
                      <div className="flex items-center gap-4">
                        {child.photo_url ? (
                          <img 
                            src={`${API}${child.photo_url}`.replace('/api/api/', '/api/')} 
                            alt={`${child.first_name} ${child.last_name}`}
                            className="w-14 h-14 sm:w-12 sm:h-12 rounded-full object-cover shrink-0"
                            data-testid={`attendance-photo-${child.id}`}
                          />
                        ) : (
                          <div className="w-14 h-14 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="font-bold text-primary text-lg sm:text-base">
                              {child.first_name.charAt(0)}{child.last_name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-base sm:text-sm">{child.first_name} {child.last_name}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm text-muted-foreground">{child.classroom}</p>
                            {child.usual_shift && (
                              <Badge variant="outline" className="hidden sm:inline-flex text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                {child.usual_shift}
                                {(() => { const st = shiftTimes.find(s => s.name === child.usual_shift); return st ? ` ${formatTime(st.start_time)}–${formatTime(st.end_time)}` : ''; })()}
                              </Badge>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 px-1 text-xs text-muted-foreground hover:text-primary"
                              onClick={(e) => { e.stopPropagation(); setMoveChildId(moveChildId === child.id ? null : child.id); setMoveTarget(''); setMoveTime(''); if (moveChildId !== child.id) fetchMoveHistory(child.id); }}
                              title="Move classroom / view history"
                              data-testid={`move-classroom-${child.id}`}
                            >
                              <ArrowRightLeft className="w-3 h-3 sm:mr-0 mr-1" />
                              <span className="sm:hidden text-xs">Move room</span>
                            </Button>
                          </div>
                          {moveChildId === child.id && (
                            <div className="mt-2 space-y-2">
                              {/* New Move */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <Select value={moveTarget} onValueChange={setMoveTarget}>
                                  <SelectTrigger className="h-7 text-xs w-32" data-testid={`move-select-${child.id}`}>
                                    <SelectValue placeholder="To room" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {classrooms.map((cr) => (
                                      <SelectItem key={cr.id} value={cr.name}>{cr.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Input 
                                  type="time" 
                                  value={moveTime} 
                                  onChange={(e) => setMoveTime(e.target.value)} 
                                  className="h-7 text-xs w-28"
                                  placeholder="Time"
                                  data-testid={`move-time-${child.id}`}
                                />
                                <Button size="sm" className="h-7 text-xs px-2" onClick={() => handleMoveClassroom(child.id)} disabled={!moveTarget} data-testid={`confirm-move-${child.id}`}>
                                  Move
                                </Button>
                              </div>
                              {/* Move History */}
                              {moveHistory[child.id] && moveHistory[child.id].length > 0 && (
                                <div className="text-xs space-y-1 border-t pt-1 mt-1">
                                  <p className="font-medium text-muted-foreground">Move History</p>
                                  {moveHistory[child.id].map((mv) => (
                                    <div key={mv.id} className="flex items-center gap-2 text-muted-foreground">
                                      {editingMove === mv.id ? (
                                        <div className="flex items-center gap-1 flex-wrap">
                                          <Input type="time" value={editMoveTime} onChange={(e) => setEditMoveTime(e.target.value)} className="h-6 text-xs w-24" />
                                          <Input value={editMoveNotes} onChange={(e) => setEditMoveNotes(e.target.value)} placeholder="Notes" className="h-6 text-xs w-24" />
                                          <Button size="sm" variant="ghost" className="h-5 text-xs px-1" onClick={() => handleEditMove(child.id, mv.id)} data-testid={`save-move-edit-${mv.id}`}>Save</Button>
                                          <Button size="sm" variant="ghost" className="h-5 text-xs px-1" onClick={() => setEditingMove(null)}>Cancel</Button>
                                        </div>
                                      ) : (
                                        <>
                                          <span>{new Date(mv.move_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                                          <span>{mv.from_classroom}</span>
                                          <span>→</span>
                                          <span>{mv.to_classroom}</span>
                                          {mv.notes && <span className="italic">({mv.notes})</span>}
                                          <Button size="sm" variant="ghost" className="h-4 px-1" onClick={() => { setEditingMove(mv.id); setEditMoveTime(new Date(mv.move_time).toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit'})); setEditMoveNotes(mv.notes || ''); }} data-testid={`edit-move-${mv.id}`}>
                                            <Edit className="w-2.5 h-2.5" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 sm:gap-4 justify-between sm:justify-end w-full sm:w-auto">
                        {record && (
                          <div className="hidden sm:block text-right text-sm">
                            <div className="flex items-center gap-1 text-green-600">
                              <LogIn className="w-4 h-4" />
                              {record.check_in_time && new Date(record.check_in_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                            {record.check_out_time && (
                              <div className="flex items-center gap-1 text-gray-600">
                                <LogOut className="w-4 h-4" />
                                {new Date(record.check_out_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                            )}
                            {record.total_hours && (
                              <div className={cn("text-xs mt-0.5 font-medium", record.over_14_hours ? "text-red-600" : "text-muted-foreground")}>
                                {record.total_hours.toFixed(1)}h
                                {record.over_14_hours && (
                                  <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">14hr!</Badge>
                                )}
                              </div>
                            )}
                            {record.transport_type && (
                              <div className="flex items-center gap-1 text-xs text-blue-600 mt-0.5">
                                <Truck className="w-3 h-3" />
                                {record.transport_type.replace(/_/g, ' ')}
                              </div>
                            )}
                            {record.checked_in_by_name && (
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                by {record.checked_in_by_name}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Compact mobile-only summary: shows check-in time inline */}
                        {record && (
                          <div className="sm:hidden text-xs text-green-700 font-medium flex items-center gap-1">
                            <LogIn className="w-3.5 h-3.5" />
                            {record.check_in_time && new Date(record.check_in_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            {record.check_out_time && (
                              <>
                                <span className="text-muted-foreground mx-0.5">→</span>
                                <LogOut className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-muted-foreground">{new Date(record.check_out_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                              </>
                            )}
                          </div>
                        )}

                        {record && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(record, child)}
                            title="Edit times"
                            data-testid={`edit-attendance-${child.id}`}
                            className="hidden sm:inline-flex"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        )}

                        {isToday && (
                          <div className="flex gap-2 flex-1 sm:flex-initial justify-end">
                            {isCheckedIn ? (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleCheckOut(child.id)}
                                data-testid={`checkout-btn-${child.id}`}
                                className="w-full sm:w-auto h-11 sm:h-9 text-base sm:text-sm font-semibold"
                              >
                                <XCircle className="w-5 h-5 sm:w-4 sm:h-4 mr-1.5" />
                                Check Out
                              </Button>
                            ) : (
                              <>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openCheckInDialog(child)}
                                  disabled={!!record}
                                  data-testid={`checkin-classroom-btn-${child.id}`}
                                  className="hidden sm:inline-flex"
                                >
                                  <Building2 className="w-4 h-4 mr-1" />
                                  Select Room
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={() => handleQuickCheckIn(child.id)}
                                  disabled={!!record}
                                  data-testid={`checkin-btn-${child.id}`}
                                  className="w-full sm:w-auto h-11 sm:h-9 text-base sm:text-sm font-semibold"
                                >
                                  <CheckCircle className="w-5 h-5 sm:w-4 sm:h-4 mr-1.5" />
                                  Check In
                                </Button>
                              </>
                            )}
                          </div>
                        )}

                        {!isToday && (
                          <span className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium",
                            record 
                              ? "bg-green-100 text-green-600" 
                              : "bg-gray-100 text-gray-600"
                          )}>
                            {record ? 'Present' : 'Absent'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{searchQuery ? `No children match "${searchQuery}"` : 'No children enrolled. Add children first to track attendance.'}</p>
                {!searchQuery && (
                  <Button 
                    className="mt-4"
                    onClick={() => window.location.href = '/children'}
                  >
                    Add Children
                  </Button>
                )}
              </div>
            );
            })()}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="card-clay">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">
                {attendance.filter(a => a.check_in_time).length}
              </p>
              <p className="text-sm text-muted-foreground">Checked In</p>
            </CardContent>
          </Card>
          <Card className="card-clay">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-600">
                {attendance.filter(a => a.check_out_time).length}
              </p>
              <p className="text-sm text-muted-foreground">Checked Out</p>
            </CardContent>
          </Card>
          <Card className="card-clay">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">
                {children.length - attendance.length}
              </p>
              <p className="text-sm text-muted-foreground">Absent</p>
            </CardContent>
          </Card>
        </div>

        {/* Check-In Dialog with Classroom Selection */}
        <Dialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Check In - Select Classroom
              </DialogTitle>
              <DialogDescription>
                Choose which classroom {selectedChild?.first_name} will be in today
              </DialogDescription>
            </DialogHeader>
            {selectedChild && (
              <div className="space-y-6 mt-4">
                {/* Child Info */}
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="font-bold text-xl text-primary">
                      {selectedChild.first_name.charAt(0)}{selectedChild.last_name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{selectedChild.first_name} {selectedChild.last_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Default classroom: {selectedChild.classroom}
                    </p>
                  </div>
                </div>

                {/* Classroom Selection */}
                <div className="space-y-3">
                  <Label>Select Classroom for Today</Label>
                  <Select value={selectedClassroom} onValueChange={setSelectedClassroom}>
                    <SelectTrigger data-testid="classroom-select">
                      <SelectValue placeholder="Select classroom" />
                    </SelectTrigger>
                    <SelectContent>
                      {classrooms.map((classroom) => (
                        <SelectItem key={classroom.id} value={classroom.name}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            <span>{classroom.name}</span>
                            <span className="text-xs text-muted-foreground">({classroom.age_range})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Quick classroom buttons */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {classrooms.slice(0, 4).map((classroom) => (
                      <Badge
                        key={classroom.id}
                        variant={selectedClassroom === classroom.name ? "default" : "outline"}
                        className="cursor-pointer hover:bg-primary/10"
                        onClick={() => setSelectedClassroom(classroom.name)}
                      >
                        {classroom.name}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Transportation Type (DCF 251) */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Truck className="w-4 h-4 text-blue-500" />
                    Arrival Method
                  </Label>
                  <Select value={transportType} onValueChange={setTransportType}>
                    <SelectTrigger data-testid="transport-type-select">
                      <SelectValue placeholder="Select arrival method (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent_dropoff">Parent Drop-off</SelectItem>
                      <SelectItem value="center_transport">Center Transportation</SelectItem>
                      <SelectItem value="walk_in">Walk-in</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Per DCF 251 — record transportation type for compliance</p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setCheckInDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={handleCheckIn}
                    disabled={!selectedClassroom}
                    data-testid="confirm-checkin-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Check In to {selectedClassroom || 'Classroom'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Print Report Dialog */}
        <Dialog open={printMode} onOpenChange={setPrintMode}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" /> DCF 251 Attendance Report
              </DialogTitle>
              <DialogDescription>Generate a DCF-compliant daily attendance record with custom date ranges</DialogDescription>
            </DialogHeader>

            {/* DCF 251 Compliance Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 mb-1">DCF 251 Attendance Requirements:</p>
              <ul className="text-blue-700 space-y-0.5 text-xs list-disc ml-4">
                <li>Daily attendance tracked for all children throughout enrollment</li>
                <li>Actual arrival and departure times recorded</li>
                <li>Transportation pickup/drop-off times documented</li>
                <li>Children may not be in care &gt; 14 hours per 24-hour period</li>
              </ul>
            </div>
            
            <div className="space-y-4 py-2">
              {/* Date Range Selector */}
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-sm font-medium">Start Date</Label>
                  <Input type="date" value={reportStartDate} onChange={e => setReportStartDate(e.target.value)} data-testid="report-start-date" className="w-[160px]" />
                </div>
                <div>
                  <Label className="text-sm font-medium">End Date</Label>
                  <Input type="date" value={reportEndDate} onChange={e => setReportEndDate(e.target.value)} data-testid="report-end-date" className="w-[160px]" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { const t = format(new Date(), 'yyyy-MM-dd'); setReportStartDate(t); setReportEndDate(t); }}>Today</Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    const now = new Date();
                    const mon = new Date(now); mon.setDate(now.getDate() - now.getDay() + 1);
                    setReportStartDate(format(mon, 'yyyy-MM-dd')); setReportEndDate(format(now, 'yyyy-MM-dd'));
                  }}>This Week</Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    const now = new Date();
                    setReportStartDate(format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'));
                    setReportEndDate(format(now, 'yyyy-MM-dd'));
                  }}>This Month</Button>
                </div>
                <Button onClick={generateReport} disabled={loadingReport} data-testid="generate-report-btn">
                  {loadingReport ? <span className="flex items-center gap-1"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Loading...</span> : 'Generate'}
                </Button>
              </div>

              {/* Report Preview */}
              {reportData.length > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{reportData.length} records found</span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={exportCSV} data-testid="export-attendance-csv">
                        <Download className="w-4 h-4 mr-1" /> CSV
                      </Button>
                      <Button size="sm" variant="outline" onClick={downloadPDF} disabled={downloadingPDF} data-testid="download-attendance-pdf">
                        <FileText className="w-4 h-4 mr-1" /> {downloadingPDF ? 'Downloading...' : 'PDF'}
                      </Button>
                      <Button size="sm" onClick={printReport} data-testid="print-report-btn">
                        <Printer className="w-4 h-4 mr-1" /> Print
                      </Button>
                    </div>
                  </div>

                  {/* 14-Hour Violations Alert */}
                  {reportData.some(r => {
                    if (!r.check_in_time || !r.check_out_time) return false;
                    return (new Date(r.check_out_time) - new Date(r.check_in_time)) / 3600000 > 14;
                  }) && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-800 text-sm">14-Hour Limit Violation Detected</p>
                        <p className="text-xs text-red-600">
                          {reportData.filter(r => r.check_in_time && r.check_out_time && (new Date(r.check_out_time) - new Date(r.check_in_time)) / 3600000 > 14).length} record(s) 
                          exceed the DCF 251 14-hour care limit. These are flagged in the report below.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Printable Report */}
                  <div className="attendance-print-area border rounded-lg">
                    <div className="p-4 border-b bg-muted/30">
                      <h2 className="font-bold text-lg">DCF 251 Daily Attendance Record — Child Care</h2>
                      <p className="text-sm text-muted-foreground">
                        {reportStartDate === reportEndDate
                          ? new Date(reportStartDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                          : `${new Date(reportStartDate + 'T12:00:00').toLocaleDateString()} — ${new Date(reportEndDate + 'T12:00:00').toLocaleDateString()}`
                        }
                      </p>
                      <p className="text-sm text-muted-foreground">{reportData.length} records | Generated {new Date().toLocaleString()}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <Table className="mobile-cards">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Child</TableHead>
                            <TableHead>Classroom</TableHead>
                            <TableHead>Arrival</TableHead>
                            <TableHead>Transport</TableHead>
                            <TableHead>Departure</TableHead>
                            <TableHead>Hours</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportData.map((r, idx) => {
                            const cin = r.check_in_time ? new Date(r.check_in_time) : null;
                            const cout = r.check_out_time ? new Date(r.check_out_time) : null;
                            const hoursNum = cin && cout ? ((cout - cin) / 3600000) : null;
                            const hours = hoursNum !== null ? hoursNum.toFixed(1) : '-';
                            const isOver14 = hoursNum !== null && hoursNum > 14;
                            return (
                              <TableRow key={idx} className={isOver14 ? 'bg-red-50' : ''}>
                                <TableCell className="text-sm">{new Date(r.date + 'T12:00:00').toLocaleDateString()}</TableCell>
                                <TableCell className="text-sm font-medium">{r.child_name}</TableCell>
                                <TableCell className="text-sm">{r.classroom || '-'}</TableCell>
                                <TableCell className="text-sm">{cin ? cin.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                                <TableCell className="text-sm text-xs">{r.transport_type ? r.transport_type.replace(/_/g, ' ') : '-'}</TableCell>
                                <TableCell className="text-sm">{cout ? cout.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Still In</Badge>}</TableCell>
                                <TableCell className={cn("text-sm font-medium", isOver14 ? "text-red-600" : "")}>{hours !== '-' ? `${hours}h` : '-'} {isOver14 && '⚠'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="p-3 border-t bg-muted/30 text-sm font-medium flex justify-between">
                      <span>Total Records: {reportData.length}</span>
                      <span>Total Hours: {reportData.reduce((sum, r) => {
                        if (!r.check_in_time || !r.check_out_time) return sum;
                        return sum + (new Date(r.check_out_time) - new Date(r.check_in_time)) / 3600000;
                      }, 0).toFixed(1)}h</span>
                    </div>
                    <div className="p-3 border-t text-xs text-muted-foreground">
                      DCF 251 — Children may not be in care for more than 14 hours in a 24-hour period unless an exception is granted.
                    </div>
                  </div>

                  {/* Classroom Movement Log */}
                  {reportMoves.length > 0 && (
                    <div className="attendance-print-area border rounded-lg mt-4">
                      <div className="p-4 border-b bg-gray-50">
                        <h2 className="font-bold text-lg">Classroom Movement Log</h2>
                        <p className="text-sm text-muted-foreground">{reportMoves.length} transitions during report period</p>
                      </div>
                      <div className="overflow-x-auto">
                        <Table className="mobile-cards">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Time</TableHead>
                              <TableHead>Child</TableHead>
                              <TableHead>From</TableHead>
                              <TableHead>To</TableHead>
                              <TableHead>Moved By</TableHead>
                              <TableHead>Notes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportMoves.map((mv, idx) => {
                              const dt = mv.move_time ? new Date(mv.move_time) : null;
                              return (
                                <TableRow key={idx}>
                                  <TableCell className="text-sm">{dt ? dt.toLocaleDateString() : '-'}</TableCell>
                                  <TableCell className="text-sm font-medium">{dt ? dt.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '-'}</TableCell>
                                  <TableCell className="text-sm font-medium">{mv.child_name || '-'}</TableCell>
                                  <TableCell className="text-sm">{mv.from_classroom || '-'}</TableCell>
                                  <TableCell className="text-sm">{mv.to_classroom || '-'}</TableCell>
                                  <TableCell className="text-sm">{mv.moved_by_name || '-'}</TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{mv.notes || '-'}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {reportData.length === 0 && !loadingReport && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Select a date range and click "Generate" to create a report
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Attendance Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[425px]" data-testid="edit-attendance-dialog">
            <DialogHeader>
              <DialogTitle>Edit Attendance Record</DialogTitle>
              <DialogDescription>
                {editRecord?.child_name} — {editRecord?.date ? new Date(editRecord.date + 'T12:00:00').toLocaleDateString() : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-checkin">Check-In Time</Label>
                <Input
                  id="edit-checkin"
                  type="time"
                  value={editCheckIn}
                  onChange={(e) => setEditCheckIn(e.target.value)}
                  data-testid="edit-checkin-time"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-checkout">Check-Out Time</Label>
                <Input
                  id="edit-checkout"
                  type="time"
                  value={editCheckOut}
                  onChange={(e) => setEditCheckOut(e.target.value)}
                  data-testid="edit-checkout-time"
                />
              </div>
              <div className="grid gap-2">
                <Label>Transport (Arrival)</Label>
                <Select value={editTransportIn} onValueChange={setEditTransportIn}>
                  <SelectTrigger data-testid="edit-transport-in">
                    <SelectValue placeholder="Select transport type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent_drop_off">Parent Drop-off</SelectItem>
                    <SelectItem value="bus">Bus</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="walk">Walk</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Transport (Departure)</Label>
                <Select value={editTransportOut} onValueChange={setEditTransportOut}>
                  <SelectTrigger data-testid="edit-transport-out">
                    <SelectValue placeholder="Select transport type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent_pick_up">Parent Pick-up</SelectItem>
                    <SelectItem value="bus">Bus</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="walk">Walk</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Input
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Optional notes"
                  data-testid="edit-notes"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="edit-cancel-btn">Cancel</Button>
              <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-save-btn">
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Print styles */}
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            .attendance-print-area, .attendance-print-area * { visibility: visible !important; }
            .attendance-print-area { position: fixed; top: 0; left: 0; width: 100%; z-index: 99999; background: white; }
            @page { margin: 0.5in; }
          }
        `}</style>
      </div>
    </DashboardLayout>
  );
};

export default AttendancePage;
